import { useState, useEffect, useCallback } from "react";
import { FunnelIcon, MagnifyingGlassIcon, PlusIcon, ArchiveBoxIcon } from "@heroicons/react/24/solid";
import debounce from "lodash/debounce";

export default function SearchBar({ partenaires, setPartenairesFiltres, onAdd, onArchives }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClasse, setSelectedClasse] = useState("Toutes les classes");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Liste des classes d’actifs
  const classesActifs = [
    "Toutes les classes",
    "Fonds Commun de Placement",
    "Fonds d'Investissement Alternatif",
    "Cryptomonnaies",
    "Produits Structurés",
    "Private Equity",
    "Produits Immobiliers",
    "Gestion Sous Mandat",
    "BUFFER ETF",
    "Dette Privée",
    "Investissements Alternatifs",
    "Services Exclusifs",
    "Assurance-Vie LUX",
  ];

  // Fonction debounce pour optimiser la recherche
  const debouncedSearch = useCallback(
    debounce((value, classe) => {
      let filtered = partenaires;

      // Filtrage par terme de recherche
      if (value) {
        filtered = filtered.filter((partenaire) =>
          Object.values(partenaire).some(
            (val) =>
              val &&
              val.toString().toLowerCase().includes(value.toLowerCase())
          )
        );
      }

      // Filtrage par classe d'actifs
      if (classe && classe !== "Toutes les classes") {
        filtered = filtered.filter((partenaire) => partenaire.classe_actifs === classe);
      }

      setPartenairesFiltres(filtered);
    }, 200),
    [partenaires, setPartenairesFiltres]
  );

  // Mise à jour des résultats lorsque `searchTerm` ou `selectedClasse` change
  useEffect(() => {
    debouncedSearch(searchTerm, selectedClasse);
    return () => debouncedSearch.cancel();
  }, [searchTerm, selectedClasse, debouncedSearch]);

  // Gestion du changement de classe d’actifs
  const handleClasseChange = (classe) => {
    setSelectedClasse(classe);
    setSearchTerm(""); // Réinitialiser le champ de recherche quand on change de classe
    setIsDropdownOpen(false);
  };

  return (
    <div className="flex items-center space-x-4 mb-4 z-10">
      {/* 🔍 Barre de recherche */}
      <div className="relative flex-1">
        <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
        <input
          type="text"
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Rechercher un partenaire..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* 📦 Bouton Archives */}
      <button
        className="flex items-center space-x-2 px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-100"
        onClick={onArchives}
      >
        <ArchiveBoxIcon className="h-5 w-5 text-gray-600" />
        <span>Archives</span>
      </button>

      {/* 📂 Menu déroulant Classe d'actifs */}
      <div className="relative">
        <button
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-100"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <FunnelIcon className="h-5 w-5 text-gray-600" />
          <span>{selectedClasse}</span>
        </button>

        {/* Dropdown menu */}
        {isDropdownOpen && (
          <div className="absolute mt-2 w-56 bg-white shadow-lg rounded-lg z-10 border border-gray-200">
            {classesActifs.map((classe, index) => (
              <button
                key={index}
                className={`block w-full text-left px-4 py-2 hover:bg-gray-100 ${
                  selectedClasse === classe ? "font-semibold text-blue-600" : "text-gray-700"
                }`}
                onClick={() => handleClasseChange(classe)}
              >
                {classe}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ➕ Bouton Ajouter */}
      <button
        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        onClick={onAdd}
      >
        <PlusIcon className="h-5 w-5 text-white" />
        <span>Nouveau partenaire</span>
      </button>
    </div>
  );
}
